import { Input } from "@foundation/components/ui/input";
import { Badge } from "@foundation/components/ui/badge";
import { Loader2, Search } from "lucide-react";
import { useMemo, useState } from "react";
import type { BoostPolicyKnowledgeDocument } from "../../types/boostPolicy";

type LibrarySelectionStepProps = {
  documents: BoostPolicyKnowledgeDocument[];
  selectedDocument: BoostPolicyKnowledgeDocument | null;
  isLoading: boolean;
  onSelect: (document: BoostPolicyKnowledgeDocument) => void;
};

export const LibrarySelectionStep = ({
  documents,
  selectedDocument,
  isLoading,
  onSelect,
}: LibrarySelectionStepProps) => {
  const [query, setQuery] = useState("");

  const filteredDocuments = useMemo(() => {
    const searchTerm = query.trim().toLowerCase();
    if (!searchTerm) {
      return documents;
    }

    return documents.filter((document) => {
      return (
        document.title.toLowerCase().includes(searchTerm) ||
        document.description.toLowerCase().includes(searchTerm) ||
        document.tags.some((tag) => tag.toLowerCase().includes(searchTerm))
      );
    });
  }, [query, documents]);

  if (isLoading && documents.length === 0) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading Revisr Library documents...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search by title, description, industry/domain tags, or region..."
          className="pl-9"
        />
      </div>

      {filteredDocuments.length === 0 ? (
        <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
          No documents match your search.
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <div className="max-h-[460px] overflow-y-auto divide-y divide-border bg-card">
            {filteredDocuments.map((document) => {
              const isSelected = selectedDocument?.id === document.id;

              return (
                <button
                  type="button"
                  key={document.id}
                  onClick={() => onSelect(document)}
                  className={`w-full text-left px-4 py-3 transition-colors ${
                    isSelected ? "bg-primary/10" : "hover:bg-muted/40"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-foreground">{document.title}</p>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {document.description || "No description available"}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {document.tags.length > 0 ? (
                          document.tags.map((tag) => (
                            <Badge
                              key={`${document.id}-${tag}`}
                              variant="outline"
                              className="text-[10px] px-2 py-0.5"
                            >
                              {tag}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-xs text-muted-foreground">No tags</span>
                        )}
                      </div>
                    </div>
                    {isSelected && (
                      <Badge className="shrink-0">Selected</Badge>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
